import { WorkspaceAsset } from '../../assets';
import { useGetQuery, useFindByQuery } from '../../graphql';
import { WorkspaceRoutine } from '../../routine';
import { RoutineType } from '../../graphql/enums';
import { useEmbeddedRoutines } from './useEmbeddedRoutines';

export const useRoutineAlerts = (
  workspaceAssetId: string,
  workspaceRoutineId: string
) => {
  const { data: workspaceAssetData, isLoading: isWorkspaceAssetLoading } =
    useGetQuery({
      modelName: 'workspaceasset',
      query: GET_WORKSPACE_ASSET,
      id: workspaceAssetId,
    });

  const { data: workspaceRoutineData, isLoading: isWorkspaceRoutineLoading } =
    useGetQuery({
      modelName: 'workspaceroutine',
      query: GET_WORKSPACE_ROUTINE,
      id: workspaceRoutineId,
    });

  const { data: routineWorkflowData, isLoading: isWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: { filter: { workspaceAsset: { eq: workspaceAssetId } } },
    });

  const wsAsset = workspaceAssetData as WorkspaceAsset | undefined;
  const directRoutines: WorkspaceRoutine[] = wsAsset?.routines ?? [];

  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } = useEmbeddedRoutines(directRoutines);

  const routineWorkflows = (routineWorkflowData?.items ?? []).filter(
    (workflow: any) =>
      workflow?.trigger?.config?.workspaceRoutineId === workspaceRoutineId
  );

  const isLoading =
    isWorkspaceAssetLoading ||
    isWorkspaceRoutineLoading ||
    isWorkflowLoading ||
    isEmbeddedRoutinesLoading;

  return {
    workspaceAsset: wsAsset
      ? ({ ...wsAsset, routines: routinesWithEmbedded } as WorkspaceAsset)
      : (undefined as unknown as WorkspaceAsset),
    workspaceRoutine: workspaceRoutineData as WorkspaceRoutine,
    routineWorkflows,
    isLoading,
    isEmbeddedRoutinesLoading,
  };
};

const GET_WORKSPACE_ASSET = `
  query getWorkspaceAsset($id: ID!) {
    getWorkspaceAsset(id: $id) {
      id
      workspace {
        id
      }
      routineState {
        workspaceRoutineId
        workspaceRoutineVersionId
        state
      }
      workflowState {
        workflowId
        triggerState
        actionState
      }
      routines {
        id
        order
        type
        config
        deactivated
        currentVersion {
          id
        }
      }
    }
  }
`;

const GET_WORKSPACE_ROUTINE = `
  query getWorkspaceRoutine($id: ID!) {
    getWorkspaceRoutine(id: $id) {
      id
      name
      workspace {
        id
      }
      type
      config
    }
  }
`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
}
`;
